import React, {useEffect, useMemo, useState} from 'react';
import styles from './styles.module.scss';
import {useForm} from 'react-hook-form';
import InputField from '../input-field';
import {SquareAsterisk, Mail} from 'lucide-react';
import {ReactComponent as EyeOpen} from 'svg/eye-open.svg';
import {ReactComponent as EyeClose} from 'svg/eye-close.svg';
import {ReactComponent as GoogleIcon} from 'svg/google.svg';
import {Button} from '@chakra-ui/react';
import classNames from 'classnames';
import Forgot from './forgot';
import AuthTerms from './auth-terms';
import AuthInfo from './auth-info';
import {signIn, signup} from 'api/auth';
import {useAuth} from 'hooks/useAuth';
import {ProgressCircleRing, ProgressCircleRoot} from '../chakra-ui/progress-circle';
import Checkbox from '../checkbox';
import {Link} from 'react-router-dom';
import {APP_PATHS, FIRST_URL, validationRules, FIRST_REFERRER} from '../../constants';
import {sendWebEvent} from 'api/user'
import {GoogleLogin} from '@react-oauth/google';
import {toaster} from 'components/chakra-ui/toaster';
import { SocialLogin } from '@capgo/capacitor-social-login';
import { Capacitor } from '@capacitor/core';
import { setupPushNotifications } from 'services/pushNotifications';
import { storeVersion, getOrDarwVersion} from 'helpers/ab/abManager';
import { ISignIn, ISignUp, IGoogleSignIn, IGoogleSignUp, IFirebaseSignIn, IFirebaseSignUp } from 'types/auth';
import { FaFacebook } from 'react-icons/fa';
import { signInWithPopup, signInWithCredential, FacebookAuthProvider } from 'firebase/auth';
import { auth, facebookProvider } from '../../config/firebase';
import { FirebaseAnalytics } from '@capacitor-firebase/analytics';
import { HandleAppInstance } from 'helpers/firebase-analytics-tracker';

interface FormData {
  email: string;
  password: string;
  confirmPrivacy: boolean;
}

const AuthForm: React.FC<{type: 'signin' | 'signup'}> = ({type}) => {
  //define UseForm hook
  const {
    handleSubmit,
    formState: {errors},
    control
  } = useForm<FormData>({
    mode: 'onSubmit'
  });

  const {login} = useAuth();
  const [loading, setLoading] = useState<boolean>(false);

  // Initialize social login for native platforms only
  useEffect(() => {
    const initializeSocialLogin = async () => {
      if (Capacitor.isNativePlatform()) {
        try {
          await SocialLogin.initialize({
            google: {
              webClientId: '10663262278-fr313efhhdtn9jjko9qta29mcskdldi4.apps.googleusercontent.com', // web client ID from constants
              mode: 'online',
            },
            facebook: {
              appId: '1292928632524497',
              clientToken: '9342eb0c1d92fdb6080ace4785050a33',
            },
          });
        } catch (error) {
          console.error('Failed to initialize social login:', error);
        }
      }
    };

    initializeSocialLogin();
  }, []);

  const onSignUp = async (data?: FormData, googleToken?: string, firebaseToken?: string) => {
    // Get user properties from localstorage
    const firstUrl = localStorage.getItem(FIRST_URL) || null;
    const firstReferrer = localStorage.getItem(FIRST_REFERRER) || null;
    const activeVersion = await getOrDarwVersion();

    // Get recaptcha token if grecaptcha is available
    let recaptchaToken: string | null = null;
    try {
      if (window.grecaptcha && window.grecaptcha.execute) {
        recaptchaToken = await new Promise<string | null>((resolve) => {
          window.grecaptcha.ready(() => {
            window.grecaptcha
              .execute('6LezTwwrAAAAAATZ5zXxYPE1cQT7JP7ucpJJLum6', { action: 'signup' })
              .then((token: string) => resolve(token))
              .catch(() => resolve(null));
          });
        });
      }
    } catch (err) {
      recaptchaToken = null;
    }

    // Construct payload before request, according to signup type
    let requestBody: ISignUp | IGoogleSignUp | IFirebaseSignUp;
    // Normal signup with email and password
    if (data) {
      requestBody = {email: data?.email,
                    password: data?.password,
                    first_url: firstUrl,
                    recaptcha_token: recaptchaToken,
                    first_referrer: firstReferrer,
                    version_id: activeVersion,
                  };
    // Google signup with Google token
    } else if (googleToken) {
      requestBody = {google_token: googleToken,
                    first_url: firstUrl,
                    first_referrer: firstReferrer,
                    recaptcha_token: recaptchaToken,
                    version_id: activeVersion,
                  };
    // Firebase signup with firebase_id_token
    } else if (firebaseToken) {
      requestBody = {firebase_id_token: firebaseToken,
                    first_url: firstUrl,
                    first_referrer: firstReferrer,
                    recaptcha_token: recaptchaToken,
                    version_id: activeVersion,
                  };
    } else {
      handleSocialError();
      throw new Error('No valid signup arguments provided');
    }
    
    // Send request to signup, if fail, will exit function early
    const res = await signup(requestBody);
    
    // Return response
    return res;
  };

  const onSignIn = async (data?: FormData, googleToken?: string, firebaseToken?: string) => {
    // Construct payload before request
    let requestBody: ISignIn | IGoogleSignIn | IFirebaseSignIn;
    if (data) {
      requestBody = {email: data.email, password: data.password};
    } else if (googleToken) {
      requestBody = {google_token: googleToken};
    } else if (firebaseToken) {
      requestBody = {firebase_id_token: firebaseToken};
    }
    else{
      handleSocialError();
      throw new Error('No valid signin arguments provided');
    }
    return await signIn(requestBody);
  };

  const onSubmit = async (data?: FormData, googleToken?: string, firebaseToken?: string) => {
    // Submit data depends on 'signin' | 'signup' condition
    setLoading(true);

    try {
      // Get API response with access token and user data
      const response = type === 'signin' ? await onSignIn(data, googleToken, firebaseToken) : await onSignUp(data, googleToken, firebaseToken);
      // Login the user to application (set token and user in context)
      login(response.access_token, response.user);
      // Update active version to make sure local storage aligned with backend
      storeVersion(response.user.version_id);
      // Push event to dataLyer (signup, will be used in GTM to fire marketing tags)
      // @ts-ignore
      window.dataLayer?.push({
        event: type === 'signup' ? 'signup' : 'signin',
      });
      // Send event to analytics (if reached here, means no errors) do not wait to response
      void sendWebEvent(type === 'signup' ? 'signup' : 'signin');
      // Setup push notifications for user (in each signup/ signin)
      void setupPushNotifications(true);
      // Fire Firebase Analytics event
      void FirebaseAnalytics.logEvent({ 
        name: type === 'signup' ? 'signup' : 'signin', 
      });
      // Save/ update firebase instance ID per user (be used to track activty in Firebase)
      void HandleAppInstance();
    } catch (error) {
      // Avoid unhandled promise rejection, do nothing (error msg will be handled in the interceptor)
    } finally{
      setLoading(false);
    }
  };

  const [show, setShow] = useState(false);

  const isSignin = type === 'signin';

  const EndElement = useMemo(() => {
    return (
      <button
        type={'button'}
        className={styles.show}
        onClick={() => {
          setShow(!show);
        }}
      >
        {show ? <EyeOpen /> : <EyeClose />}
      </button>
    );
  }, [show]);


  const handleSocialError = () => {
    // Display toast error
    toaster.create({
      description: "Something went wrong, please try again or contact support.",
      duration: 5000,
      type: 'warning',
      action: {
        label: 'Close',
        onClick: () => {}
      }
    });
  };

  const handleNativeGoogleSignIn = async () => {
    try {
      setLoading(true);
      
      const result = await SocialLogin.login({
        provider: 'google',
        options: {
        // Keep empty - using defaults email, profile, and access token
        },
      });
      // Extract the Google ID token from the result (it will be sent to the backend, and the backend will use it to get the access token and user data via Google API)
      let googleToken: string | null = null;
      if ('idToken' in result.result && result.result.idToken) {
        googleToken = result.result.idToken;
      }
      // else if ('accessToken' in result.result && result.result.accessToken) {
      //   // For online mode, we might need to use access token or make additional call, we might consider this in the future
      //   googleToken = result.result.accessToken?.token || null;
      // }
      
      if (googleToken) {
        await onSubmit(undefined, googleToken, undefined);
      } else {
        throw new Error('No token received from Google authentication');
      }
    } catch (error) {
      handleSocialError();
    } finally {
      setLoading(false);
    }
  };

  // For web, use login with facebook (firebase)
  const handleWebFacebookSignIn = async () => {
    try {
      setLoading(true);
      // Sign in/ up to facebook via Firebase
      const result = await signInWithPopup(auth, facebookProvider);
      // Get Firebase ID token
      const firebaseIdToken = await result?.user?.getIdToken();  // This is more precisly the firebase ID token, which reprsents the fb user
      if (!firebaseIdToken) {
        throw new Error('No token received from firbase (fb authentication)');
      }
      await onSubmit(undefined, undefined, firebaseIdToken);
      // Success is handled in onSubmit
    } catch (error) {
      toaster.create({
        description: "Facebook login failed. Please try again or contact support.",
        duration: 5000,
        type: 'warning',
        action: {
          label: 'Close',
          onClick: () => {}
        }
      });
    } finally {
      setLoading(false);
    }
  };

  // For native platforms, use Ioinc Social Login
  const handleNativeFacebookSignIn = async () => {
    try {
      setLoading(true);
      // Sign in/ up to facebook via social login
      const result = await SocialLogin.login({
        provider: 'facebook',
        options: {
          permissions: ['email', 'public_profile'],
        },
      });
      // Extract the Facebook ID token from the result
      const facebookToken = result.result.accessToken?.token;
      if (!facebookToken) {
        throw new Error('No token received from Facebook authentication');
      }

      // Create Firebase credential from the fb access token (exchange the fb access token with the Firebase ID token)
      const credential = FacebookAuthProvider.credential(facebookToken);
      // Sign in/up to Firebase with the Facebook credential
      const firebaseResult = await signInWithCredential(auth, credential)
      // Get Firebase ID token
      const firebaseIdToken = await firebaseResult.user.getIdToken();
      // Call onSubmit with the Facebook token
      await onSubmit(undefined, undefined, firebaseIdToken);
    } catch (error) {
      handleSocialError();
    } finally {
      setLoading(false);
    }
  };


  // Get the width of the window and set it to the google button width, minimum 40px for padding
  const [googleWidth, setGoogleWidth] = useState('420'); // fallback default
  useEffect(() => {
    // Width minimum 200px, maximum 400px
    const width = Math.max(200, Math.min(window.innerWidth - 40, 400));
    setGoogleWidth(width.toString());
  }, []); // empty dependency array = runs only once when component mounts
  
  return (
    <div className={styles.wrapper}>
      <p className={styles.title}>{isSignin ? 'Sign In' : 'Sign Up'}</p>
        
          {isSignin ? <></>
           : <p className={styles.signupGiftText}>Join today and get <span>$5.00</span> welcome gift! 🎁</p>
           }
        <div className={styles.socialSignWrapper}>
          {Capacitor.isNativePlatform() ? (
            // Native button for Capacitor
            <Button
              variant="outline"
              className={styles.nativeGoogleButton}
              onClick={handleNativeGoogleSignIn}
              type="button"
              disabled={loading}
              width={`${googleWidth}px`}
            >
              <GoogleIcon />
              {isSignin ? 'Sign in with Google' : 'Sign up with Google'}
            </Button>
          ) : (
            // Web GoogleLogin for browser (keep existing implementation intact)
            <div className={styles.googleSignInnerWrapper} style={{colorScheme: 'light'}}>
              <GoogleLogin
                onSuccess={(tokenResponse) => {
                  onSubmit(undefined, tokenResponse.credential, undefined);  // tokenResponse.credential is the Google ID token, it will be sent to the backend, and the backend will use it to get the access token and user data via Google API
                }}
                onError={handleSocialError}
                useOneTap={false}
                auto_select={false}
                type='standard'
                theme="outline"
                size='large'
                shape="rectangular"
                text={isSignin ? 'signin_with' : 'signup_with'}
                logo_alignment='center'
                width={googleWidth}
                locale="en_US"
              />
            </div>
          )}
        </div>
        
        {/* Facebook Login Button */}
        <div className={styles.socialSignWrapper}>
          <Button
            colorScheme="facebook"
            variant="solid"
            className={styles.facebookButton}
            onClick={Capacitor.isNativePlatform() ? handleNativeFacebookSignIn : handleWebFacebookSignIn}
            type="button"
            disabled={loading}
            width={`${googleWidth}px`}
          >
          <>
            <FaFacebook />
            {`${isSignin ? 'Sign in' : 'Sign up'} with Facebook`}
          </>
          </Button>
        </div>
        
      <div className={styles.delimiter}>
        <hr className={styles.hr}/>
        <span>or continue with</span>
        <hr className={styles.hr}/>
      </div>
      <form>
        <div className={styles.fieldWrapper}>
          <InputField
            control={control}
            name={'email'}
            type={'text'}
            placeholder={'Type your email here...'}
            label={'Email'}
            startElement={<Mail className={styles.fieldIcon}/>}
            rules={{
              required: {value: true, message: 'Email is required'},
              ...validationRules.email
            }}
            error={errors['email']?.message || ''}
          />
        </div>
        <div className={styles.fieldWrapper}>
          <InputField
            control={control}
            name={'password'}
            type={show ? 'text' : 'password'}
            placeholder={'Type your password here...'}
            label={'Password'}
            startElement={<SquareAsterisk className={styles.fieldIcon}/>}
            endElement={EndElement}
            rules={{
              required: {value: true, message: 'Password is required'},
              ...validationRules.password
            }}
            error={errors['password']?.message || ''}
          />
        </div>
        {isSignin && (
          <div className={styles.fieldWrapper}>
            <Forgot />
          </div>
        )}
        {!isSignin && (
          <>
            <div className={styles.privacy}>
              <Checkbox
                control={control}
                label={''}
                name={'confirmPrivacy'}
                rules={{required: 'You must agree privacy policy and terms of service'}}
              />
              <p className={styles.privacyText}>
                I agree to the{' '}
                <Link to={APP_PATHS.PRIVACY} className={styles.linkGreen}>
                  Privacy Policy{' '}
                </Link>
                and{' '}
                <Link to={APP_PATHS.TERMS} className={styles.linkGreen}>
                  Terms of Service
                </Link>
              </p>
            </div>
            <p className={styles.error}>
              {errors['confirmPrivacy']?.message && (errors['confirmPrivacy']?.message as string)}
            </p>
          </>
        )}
        <div className={styles.fieldWrapper}>
          <Button
            variant="outline"
            className={classNames(styles.btn, styles.green)}
            type="button"
            onClick={handleSubmit((data) => onSubmit(data, undefined, undefined))}
            disabled={loading}
          >
            {loading ? (
              <ProgressCircleRoot value={null} size="xs">
                <ProgressCircleRing cap="round" />
              </ProgressCircleRoot>
            ) : isSignin ? (
              'Sign In'
            ) : (
              'Sign Up'
            )}
          </Button>
        </div>
      </form>
      <div className={styles.fieldWrapper}>
        <div className={styles.subtext}>
          {isSignin ? 'Not registered?' : 'Have an account already?'}
          <Link to={isSignin ? APP_PATHS.SIGN_UP : APP_PATHS.LOGIN} className={styles.link}>
            {isSignin ? ' Sign up' : ' Sign in'}
          </Link>
        </div>
      </div>
      {isSignin ? <AuthTerms /> : <AuthInfo />}
    </div>
  );
};

export default AuthForm;
